// Order Management Constants
// Based on the Orders API Integration documentation

// ==================== ORDER STATUSES ====================
// Canonical order lifecycle. Generation/printing details live on generation records.
export const ORDER_STATUS = {
  PENDING_CONFIRMATION: 'pending_confirmation',
  PENDING_GENERATION: 'pending_generation',
  PENDING_PRINTING: 'pending_printing',
  PENDING_DELIVERY: 'pending_delivery',
  PICKED_UP: 'picked_up',
  DELIVERED: 'delivered',
  RETURNED: 'returned',
  CANCELLED: 'cancelled'
};

export const OFFLINE_ORDER_STATUS = {
  PLACED: 'placed',
  PREVIEW_READY: 'preview_ready',
  PREVIEW_SENT: 'preview_sent',
  CUSTOMER_APPROVED: 'customer_approved',
  GENERATING: 'generating',
  GENERATED: 'generated',
  REVIEWER_ASSIGNED: 'reviewer_assigned',
  UNDER_REVIEW: 'under_review',
  ADMIN_REVIEW: 'admin_review',
  APPROVED: 'approved',
  READY_FOR_PRINT: 'ready_for_print',
  PRINTED: 'printed',
  SHIPPING: 'shipping',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled'
};

// Order status display labels
export const ORDER_STATUS_LABELS = {
  [ORDER_STATUS.PENDING_CONFIRMATION]: 'Pending Confirmation',
  [ORDER_STATUS.PENDING_GENERATION]: 'Pending Generation',
  [ORDER_STATUS.PENDING_PRINTING]: 'Pending Printing',
  [ORDER_STATUS.PENDING_DELIVERY]: 'Pending Delivery',
  [ORDER_STATUS.PICKED_UP]: 'Picked Up',
  [ORDER_STATUS.DELIVERED]: 'Delivered',
  [ORDER_STATUS.RETURNED]: 'Returned',
  [ORDER_STATUS.CANCELLED]: 'Cancelled',

  // Read-only rollout fallbacks for orders created before the lifecycle migration.
  placed: 'Order Placed',
  preview_ready: 'Preview Ready',
  preview_sent: 'Preview Sent',
  customer_approved: 'Customer Approved',
  generating: 'Generating',
  generated: 'Generated',
  reviewer_assigned: 'Reviewer Assigned',
  under_review: 'Under Review',
  admin_review: 'Admin Review',
  approved: 'Approved',
  ready_for_print: 'Ready for Print',
  printed: 'Printed',
  shipping: 'Shipping',
  shipped: 'Shipped'
};

// Order status colors for UI components
export const ORDER_STATUS_COLORS = {
  [ORDER_STATUS.PENDING_CONFIRMATION]: 'warning',
  [ORDER_STATUS.PENDING_GENERATION]: 'attention',
  [ORDER_STATUS.PENDING_PRINTING]: 'info',
  [ORDER_STATUS.PENDING_DELIVERY]: 'highlight',
  [ORDER_STATUS.PICKED_UP]: 'info',
  [ORDER_STATUS.DELIVERED]: 'success',
  [ORDER_STATUS.RETURNED]: 'warning',
  [ORDER_STATUS.CANCELLED]: 'critical',

  // Read-only rollout fallbacks.
  placed: 'warning',
  preview_ready: 'info',
  preview_sent: 'highlight',
  customer_approved: 'success',
  generating: 'attention',
  generated: 'attention',
  reviewer_assigned: 'highlight',
  under_review: 'attention',
  admin_review: 'attention',
  approved: 'success',
  ready_for_print: 'highlight',
  printed: 'success',
  shipping: 'highlight',
  shipped: 'info'
};

// ==================== ORDER PRIORITIES ====================
export const ORDER_PRIORITY = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent'
};

// Priority display labels
export const ORDER_PRIORITY_LABELS = {
  [ORDER_PRIORITY.LOW]: 'Low',
  [ORDER_PRIORITY.NORMAL]: 'Normal',
  [ORDER_PRIORITY.HIGH]: 'High',
  [ORDER_PRIORITY.URGENT]: 'Urgent'
};

// Priority colors for UI components
export const ORDER_PRIORITY_COLORS = {
  [ORDER_PRIORITY.LOW]: 'subdued',
  [ORDER_PRIORITY.NORMAL]: 'info',
  [ORDER_PRIORITY.HIGH]: 'warning',
  [ORDER_PRIORITY.URGENT]: 'critical'
};

// ==================== FLAG TYPES ====================
export const FLAG_TYPE = {
  QUALITY_ISSUE: 'quality_issue',
  CUSTOMER_REQUEST: 'customer_request',
  HELP_REQUEST: 'help_request',
  TECHNICAL_PROBLEM: 'technical_problem',
  URGENT_DELIVERY: 'urgent_delivery',
  SPECIAL_INSTRUCTIONS: 'special_instructions',
  PAYMENT_ISSUE: 'payment_issue',
  SHIPPING_CONCERN: 'shipping_concern',
  REVIEW_NEEDED: 'review_needed'
};

// Flag type display labels
export const FLAG_TYPE_LABELS = {
  [FLAG_TYPE.QUALITY_ISSUE]: 'Quality Issue',
  [FLAG_TYPE.CUSTOMER_REQUEST]: 'Customer Request',
  [FLAG_TYPE.HELP_REQUEST]: 'Help Request',
  [FLAG_TYPE.TECHNICAL_PROBLEM]: 'Technical Problem',
  [FLAG_TYPE.URGENT_DELIVERY]: 'Urgent Delivery',
  [FLAG_TYPE.SPECIAL_INSTRUCTIONS]: 'Special Instructions',
  [FLAG_TYPE.PAYMENT_ISSUE]: 'Payment Issue',
  [FLAG_TYPE.SHIPPING_CONCERN]: 'Shipping Concern',
  [FLAG_TYPE.REVIEW_NEEDED]: 'Review Needed'
};

// Flag type colors for UI components
export const FLAG_TYPE_COLORS = {
  [FLAG_TYPE.QUALITY_ISSUE]: 'critical',
  [FLAG_TYPE.CUSTOMER_REQUEST]: 'info',
  [FLAG_TYPE.HELP_REQUEST]: 'warning',
  [FLAG_TYPE.TECHNICAL_PROBLEM]: 'warning',
  [FLAG_TYPE.URGENT_DELIVERY]: 'critical',
  [FLAG_TYPE.SPECIAL_INSTRUCTIONS]: 'highlight',
  [FLAG_TYPE.PAYMENT_ISSUE]: 'warning',
  [FLAG_TYPE.SHIPPING_CONCERN]: 'attention',
  [FLAG_TYPE.REVIEW_NEEDED]: 'info'
};

// ==================== WORKFLOW STAGES ====================
export const WORKFLOW_STAGES = [
  {
    id: 'confirmation',
    label: 'Confirmation',
    statuses: [ORDER_STATUS.PENDING_CONFIRMATION, 'placed', 'preview_ready', 'preview_sent'],
    color: 'warning'
  },
  {
    id: 'generation',
    label: 'Generation',
    statuses: [
      ORDER_STATUS.PENDING_GENERATION,
      'customer_approved',
      'generating',
      'generated',
      'reviewer_assigned',
      'under_review',
      'admin_review',
      'approved'
    ],
    color: 'attention'
  },
  {
    id: 'printing',
    label: 'Printing',
    statuses: [ORDER_STATUS.PENDING_PRINTING, 'ready_for_print', 'printed'],
    color: 'info'
  },
  {
    id: 'delivery',
    label: 'Delivery',
    statuses: [ORDER_STATUS.PENDING_DELIVERY, ORDER_STATUS.PICKED_UP, 'shipping', 'shipped'],
    color: 'highlight'
  },
  {
    id: 'complete',
    label: 'Complete',
    statuses: [ORDER_STATUS.DELIVERED, ORDER_STATUS.RETURNED],
    color: 'success'
  }
];

// ==================== FILTER OPTIONS ====================
export const FILTER_OPTIONS = {
  STATUS: Object.values(ORDER_STATUS).map(status => ({
    value: status,
    label: ORDER_STATUS_LABELS[status]
  })),

  PRIORITY: Object.values(ORDER_PRIORITY).map(priority => ({
    value: priority,
    label: ORDER_PRIORITY_LABELS[priority]
  })),

  FLAG_TYPE: Object.values(FLAG_TYPE).map(flagType => ({
    value: flagType,
    label: FLAG_TYPE_LABELS[flagType]
  })),

  HAS_FLAGS: [
    { value: '', label: 'All Orders' },
    { value: 'true', label: 'With Flags' },
    { value: 'false', label: 'Without Flags' }
  ],

  SOURCE: [
    { value: 'shopify', label: 'Shopify' },
    { value: 'manual', label: 'Manual' }
  ],

  PAYMENT_METHOD: [
    { value: 'cod', label: 'Cash on Delivery' },
    { value: 'online_payment', label: 'Online Payment' },
    { value: 'instapay', label: 'InstaPay' },
    { value: 'mobile_wallet', label: 'Mobile Wallet' },
    { value: 'bank_transfer', label: 'Bank Transfer' }
  ],

  PAYMENT_STATUS: [
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'collected', label: 'Collected' },
    { value: 'refunded', label: 'Refunded' }
  ],

  FINANCIAL_STATUS: [
    { value: 'pending', label: 'Pending' },
    { value: 'authorized', label: 'Authorized' },
    { value: 'partially_paid', label: 'Partially Paid' },
    { value: 'paid', label: 'Paid' },
    { value: 'partially_refunded', label: 'Partially Refunded' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'voided', label: 'Voided' }
  ],

  DATE_RANGES: [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'last_90_days', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ],

  SORT_OPTIONS: [
    { value: 'created_at_desc', label: 'Newest First' },
    { value: 'created_at_asc', label: 'Oldest First' },
    { value: 'updated_at_desc', label: 'Recently Updated' },
    { value: 'updated_at_asc', label: 'Least Recently Updated' },
    { value: 'priority_desc', label: 'High Priority First' },
    { value: 'priority_asc', label: 'Low Priority First' },
    { value: 'total_desc', label: 'Highest Value First' },
    { value: 'total_asc', label: 'Lowest Value First' },
    { value: 'customer_name_asc', label: 'Customer Name (A-Z)' },
    { value: 'customer_name_desc', label: 'Customer Name (Z-A)' },
    { value: 'order_id_asc', label: 'Order ID (Ascending)' },
    { value: 'order_id_desc', label: 'Order ID (Descending)' }
  ]
};

// ==================== QUICK FILTER PRESETS ====================
export const QUICK_FILTERS = {
  URGENT_ORDERS: {
    name: 'Urgent Orders',
    filters: {
      priorities: ['urgent', 'high'],
      statuses: [
        ORDER_STATUS.PENDING_CONFIRMATION,
        ORDER_STATUS.PENDING_GENERATION,
        ORDER_STATUS.PENDING_PRINTING,
        ORDER_STATUS.PENDING_DELIVERY
      ]
    }
  },
  FLAGGED_ORDERS: {
    name: 'Flagged Orders',
    filters: {
      has_flags: 'true'
    }
  },
  PENDING_APPROVAL: {
    name: 'Pending Confirmation',
    filters: {
      statuses: [ORDER_STATUS.PENDING_CONFIRMATION]
    }
  },
  READY_TO_SHIP: {
    name: 'Pending Delivery',
    filters: {
      statuses: [ORDER_STATUS.PENDING_DELIVERY, ORDER_STATUS.PICKED_UP]
    }
  },
  PAYMENT_ISSUES: {
    name: 'Payment Issues',
    filters: {
      payment_status: ['pending']
    }
  }
};

// ==================== PAGINATION DEFAULTS ====================
export const PAGINATION_DEFAULTS = {
  PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_PAGE_SIZE: 100
};

// ==================== ANALYTICS PERIODS ====================
export const ANALYTICS_PERIODS = {
  '1d': 'Last 24 Hours',
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
  '90d': 'Last 90 Days',
  '1y': 'Last Year'
};

// ==================== WEBSOCKET EVENTS ====================
export const WEBSOCKET_EVENTS = {
  ORDER_CREATED: 'order_created',
  ORDER_UPDATED: 'order_updated',
  ORDER_STATUS_CHANGED: 'order_status_changed',
  ORDER_ASSIGNED: 'order_assigned',
  ORDER_COMMENT_ADDED: 'order_comment_added',
  ORDER_FLAG_ADDED: 'order_flag_added',
  REVIEWER_WORKLOAD_UPDATED: 'reviewer_workload_updated'
};

// ==================== API RATE LIMITS ====================
export const RATE_LIMITS = {
  REQUESTS_PER_MINUTE: 100,
  BURST_LIMIT: 20,
  RETRY_AFTER_MS: 60000 // 1 minute
};

// ==================== VALIDATION RULES ====================
export const VALIDATION_RULES = {
  ORDER_ID: {
    pattern: /^[a-zA-Z0-9_-]+$/,
    minLength: 1,
    maxLength: 50
  },
  CUSTOMER_EMAIL: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    maxLength: 255
  },
  CUSTOMER_NAME: {
    minLength: 1,
    maxLength: 100
  },
  COMMENT_TEXT: {
    minLength: 1,
    maxLength: 1000
  },
  FLAG_DESCRIPTION: {
    minLength: 1,
    maxLength: 500
  }
};

// ==================== MANUAL ACTIONS ====================
export const MANUAL_ACTION_TYPE = {
  WHATSAPP_SEND: 'whatsapp_send',
  TRIAL_REGENERATE: 'trial_regenerate',
  STATUS_CHANGE: 'status_change'
};

export const MANUAL_ACTION_LABELS = {
  [MANUAL_ACTION_TYPE.WHATSAPP_SEND]: 'Send WhatsApp',
  [MANUAL_ACTION_TYPE.TRIAL_REGENERATE]: 'Regenerate Trial',
  [MANUAL_ACTION_TYPE.STATUS_CHANGE]: 'Change Status'
};

export const MANUAL_ACTION_PERMISSIONS = {
  [MANUAL_ACTION_TYPE.WHATSAPP_SEND]: 'orders:send_whatsapp',
  [MANUAL_ACTION_TYPE.TRIAL_REGENERATE]: 'trials:regenerate',
  [MANUAL_ACTION_TYPE.STATUS_CHANGE]: 'orders:update_status'
};

// Manual action availability based on order status
export const MANUAL_ACTION_AVAILABILITY = {
  [MANUAL_ACTION_TYPE.WHATSAPP_SEND]: [ORDER_STATUS.PENDING_CONFIRMATION],
  [MANUAL_ACTION_TYPE.TRIAL_REGENERATE]: [
    ORDER_STATUS.PENDING_CONFIRMATION,
    ORDER_STATUS.PENDING_GENERATION
  ]
};

// ==================== ERROR CODES ====================
export const ERROR_CODES = {
  ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
  INVALID_STATUS_TRANSITION: 'INVALID_STATUS_TRANSITION',
  REVIEWER_NOT_AVAILABLE: 'REVIEWER_NOT_AVAILABLE',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  SERVER_ERROR: 'SERVER_ERROR'
};

// ==================== UTILITY FUNCTIONS ====================
const CANONICAL_ORDER_TRANSITIONS = {
  [ORDER_STATUS.PENDING_CONFIRMATION]: [ORDER_STATUS.PENDING_GENERATION, ORDER_STATUS.CANCELLED],
  [ORDER_STATUS.PENDING_GENERATION]: [ORDER_STATUS.PENDING_PRINTING, ORDER_STATUS.CANCELLED],
  [ORDER_STATUS.PENDING_PRINTING]: [ORDER_STATUS.PENDING_DELIVERY, ORDER_STATUS.CANCELLED],
  [ORDER_STATUS.PENDING_DELIVERY]: [ORDER_STATUS.PICKED_UP, ORDER_STATUS.CANCELLED],
  [ORDER_STATUS.PICKED_UP]: [ORDER_STATUS.DELIVERED, ORDER_STATUS.RETURNED],
  [ORDER_STATUS.DELIVERED]: [ORDER_STATUS.RETURNED],
  [ORDER_STATUS.RETURNED]: [],
  [ORDER_STATUS.CANCELLED]: []
};

const OFFLINE_ORDER_TRANSITIONS = {
  placed: ['preview_ready', 'cancelled'],
  preview_ready: ['preview_sent', 'cancelled'],
  preview_sent: ['customer_approved', 'cancelled'],
  customer_approved: ['generating', 'cancelled'],
  generating: ['generated', 'cancelled'],
  generated: ['reviewer_assigned', 'cancelled'],
  reviewer_assigned: ['under_review', 'cancelled'],
  under_review: ['admin_review', 'generating', 'cancelled'],
  admin_review: ['approved', 'under_review', 'cancelled'],
  approved: ['ready_for_print'],
  ready_for_print: ['printed'],
  printed: ['shipping'],
  shipping: ['shipped'],
  shipped: ['delivered'],
  delivered: [],
  cancelled: []
};

export const orderUtils = {
  // Check if status transition is valid
  isValidStatusTransition(currentStatus, newStatus, source = null) {
    const validTransitions = source === 'offline'
      ? OFFLINE_ORDER_TRANSITIONS
      : CANONICAL_ORDER_TRANSITIONS;
    return validTransitions[currentStatus]?.includes(newStatus) || false;
  },

  // Get next possible statuses for current status
  getNextStatuses(currentStatus, source = null) {
    const validTransitions = source === 'offline'
      ? OFFLINE_ORDER_TRANSITIONS
      : CANONICAL_ORDER_TRANSITIONS;
    return validTransitions[currentStatus] || [];
  },

  // Get workflow stage for status
  getWorkflowStage(status) {
    return WORKFLOW_STAGES.find(stage =>
      stage.statuses.includes(status)
    ) || null;
  },

  // Calculate order progress percentage
  getOrderProgress(status) {
    const progressMap = {
      [ORDER_STATUS.PENDING_CONFIRMATION]: 10,
      [ORDER_STATUS.PENDING_GENERATION]: 30,
      [ORDER_STATUS.PENDING_PRINTING]: 55,
      [ORDER_STATUS.PENDING_DELIVERY]: 75,
      [ORDER_STATUS.PICKED_UP]: 90,
      [ORDER_STATUS.DELIVERED]: 100,
      [ORDER_STATUS.RETURNED]: 100,
      [ORDER_STATUS.CANCELLED]: 0,

      // Read-only rollout fallbacks.
      placed: 10,
      preview_ready: 20,
      preview_sent: 25,
      customer_approved: 30,
      generating: 40,
      generated: 50,
      reviewer_assigned: 55,
      under_review: 60,
      admin_review: 65,
      approved: 70,
      ready_for_print: 75,
      printed: 80,
      shipping: 85,
      shipped: 90
    };

    return progressMap[status] ?? 0;
  },

  getStatusLabel(status) {
    return ORDER_STATUS_LABELS[status] || String(status || 'Unknown')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, character => character.toUpperCase());
  },

  // Explicit order.source is authoritative. Tags are only a rollout fallback.
  getSourceDisplay(order) {
    const source = String(order?.source || '').trim().toLowerCase();
    if (source) {
      const sourceLabels = {
        shopify: 'Shopify',
        manual: 'Manual',
        api: 'API',
        import: 'Import',
        offline: 'Offline'
      };

      return {
        label: sourceLabels[source] || source
          .replace(/_/g, ' ')
          .replace(/\b\w/g, character => character.toUpperCase()),
        color: source === 'shopify' ? 'success' : 'info'
      };
    }

    const tags = Array.isArray(order?.tags) ? order.tags.join(',') : String(order?.tags || '');
    const isLegacyManual = tags.includes('source:manual') && !tags.includes('CRCOD');
    return isLegacyManual
      ? { label: 'Manual', color: 'info' }
      : { label: 'Shopify', color: 'success' };
  }
};